import { openMinicart } from '@components/minicart';
import { initProductForm } from '@components/product';
import { addToWishlist, deleteFromWishlist, getWishlist } from '@components/wishlist';
import { execOnLoad, formatPrice } from '@general/helpers';
import { getProduct } from '@services/product';
import breakpoints from 'breakpoints';
import { throttle } from 'lodash';
import Swiper from 'swiper';
import { EffectFade, Manipulation, Navigation, Pagination } from 'swiper/modules';
import 'swiper/scss';
import 'swiper/scss/navigation';

const ITSELF = 'itself';

// Each key in object is the key returned by the product API
// and the value is the selector used in the product tile snippet.
// This is used to map the product data to the DOM elements in the product tile.
const SELECTORS = {
  api: {
    compare_at_price: '.product-tile__price [data-compare-at-price]',
    handle: ITSELF,
    id: ITSELF,
    price: '.product-tile__price [data-price]',
    tags: '.product-tags',
    title: '.product-tile__title',
    url: '.product-tile__title[href]',
  },
  colorSwatches: '.get-colors__swatch',
  productTileGallery: '.product-tile__gallery',
  wishlistButton: '.product-tile__wishlist[data-wishlist-btn]',
  shoppableMarket: 'marketShoppable',
};

const checkWishlistStatus = async (wishlistButton, product, productVariant) => {
  if (!wishlistButton || !product) return;

  const wishlist = await getWishlist();
  if (!wishlist) return;

  const variant = productVariant || product.variants[0];

  const wishlistProduct = wishlist?.listcontents?.find(
    (addedProduct) => addedProduct.epi == variant.id && addedProduct.empi == product.id,
  );

  wishlistButton.dataset.withEpi = true;
  wishlistButton.dataset.wishlistStatus = wishlistProduct ? 'active' : 'inactive';
  wishlistButton.dataset.wishlistProductId = product.id;
  wishlistButton.dataset.wishlistProductUrl = `${window.Shopify.shop}${product.url}${productVariant ? `?variant=${variant.id}` : ''}`;
  wishlistButton.dataset.wishlistVariantId = variant.id;
  wishlistButton.dataset.wishlistPrice = formatPrice({
    value: variant.price,
  });
  wishlistButton.dataset.wishlistCompareAtPrice = variant.compare_at_price
    ? formatPrice({
        value: variant.compare_at_price,
      })
    : '';
};

const manageSwatchColors = ({ tile, tileGallery }) => {
  const colorSwatches = tile.querySelectorAll(SELECTORS.colorSwatches);
  if (!colorSwatches || colorSwatches.length === 0) return;

  const variantsCache = {};

  colorSwatches.forEach((swatch) => {
    if (typeof swatch.dataset.productHandle !== 'undefined') {
      const productHandle = swatch.dataset.productHandle;
      const variantId = swatch.dataset.variantId;
      const imageContainer = document.createElement('div');
      imageContainer.classList.add('product-tile-images__single-image', 'swiper-slide');

      swatch.addEventListener('click', () => {
        if (swatch.classList.contains('get-colors__swatch--active')) return;

        const loadProduct = (product) => {
          const productVariant = product.variants.find((variant) => variant.id == variantId);
          if (!productVariant) return;
          if (!product?.media?.length) return;

          const imagesContainer = tile.querySelector(SELECTORS.productTileGallery);
          imagesContainer.classList.add('loading');
          // regex to check if `sku` is included in any of the media src
          const regex = new RegExp(productVariant.sku, 'i');
          const variantMedia = product.media.filter(
            ({ src, preview_image }) => src?.match(regex) || preview_image?.src?.match(regex),
          );
          const images =
            variantMedia.length > 0
              ? variantMedia
              : [
                  {
                    alt: 'Placeholder',
                    src: window.dAgency.__dBlank.mediaPlaceholder,
                  },
                ];
          const maxImages = 99; // Limit the number of images to avoid performance issues

          tileGallery.removeAllSlides();
          images.forEach((image, index) => {
            if (index > maxImages) return;

            const linkWrapper = document.createElement('a');
            const mediaContainer = document.createElement('div');
            linkWrapper.classList.add(`${SELECTORS.productTileGallery.split('.')[1]}-link`);
            linkWrapper.setAttribute('href', `${product.url}?variant=${productVariant.id}`);
            mediaContainer.classList.add('media__container');
            const imgTag = document.createElement('img');
            imgTag.setAttribute('alt', image.alt);
            imgTag.setAttribute('src', image.src);
            imgTag.classList.add('media', 'product__image');

            const newSlide = imageContainer.cloneNode(false);
            newSlide.appendChild(linkWrapper);
            linkWrapper.appendChild(mediaContainer);
            mediaContainer.appendChild(imgTag);
            tileGallery.appendSlide(newSlide);
          });

          setTimeout(() => {
            imagesContainer.classList.remove('loading');
            tileGallery.update();
          }, 300);

          swatch.classList.add('get-colors__swatch--active');

          colorSwatches.forEach((s) => {
            if (s !== swatch) {
              s.classList.remove('get-colors__swatch--active');
            }
          });

          const shoppableMarket = tile.dataset[SELECTORS.shoppableMarket];

          // Update product tile info
          Object.entries(SELECTORS.api).forEach(([key, value]) => {
            const element = value === ITSELF ? tile : tile.querySelector(value);
            const wishlistButton = tile.querySelector(SELECTORS.wishlistButton);

            if (element) {
              switch (key) {
                case 'tags': {
                  // save inner div 'product-tile__tags--variants' before overwriting it
                  const allVariantTags = element.querySelectorAll('.product-tags__tag--variant');
                  // keep main product tags (variants do not have tags)
                  element.innerHTML = product[key]
                    .map((tag) => {
                      if (tag.startsWith('TAG__')) {
                        return `<span class="product-tags__tag product-tags__tag--${tag.replace('TAG__', '').toLowerCase()}">${tag.replace('TAG__', '')}</span>`;
                      }
                      return '';
                    })
                    .join('');
                  if (shoppableMarket === 'true') {
                    if (productVariant.compare_at_price && productVariant.price) {
                      const discountPercentage = Math.round(
                        ((productVariant.compare_at_price - productVariant.price) / productVariant.compare_at_price) *
                          100,
                      );
                      if (discountPercentage > 0) {
                        element.innerHTML += `<span class="product-tags__tag product-tags__tag--sale">-${discountPercentage}%</span>`;
                      }
                    }
                    if (!productVariant.available) {
                      element.innerHTML += `<span class="product-tags__tag product-tags__tag--out-of-stock">${
                        element.dataset.outOfStock
                      }</span>`;
                    }
                  }
                  if (allVariantTags) {
                    element.append(...allVariantTags);
                    // remove the class 'active' from all tags
                    allVariantTags.forEach((tag) => tag.classList.remove('active'));
                    // add the class 'active' to the tag
                    const activeTags = element.querySelectorAll(
                      `.product-tags__tag--variant[data-variant-id="${productVariant.id}"]`,
                    );
                    if (activeTags.length > 0) {
                      activeTags.forEach((tag) => tag.classList.add('active'));
                    }
                  }
                  break;
                }

                case 'compare_at_price':
                  element.textContent = productVariant.compare_at_price
                    ? formatPrice({ value: productVariant.compare_at_price })
                    : '';
                  break;

                case 'price':
                  element.textContent = formatPrice({ value: productVariant.price });
                  break;

                case 'title':
                  // keep the title of the product
                  element.textContent = product.title;
                  break;

                case 'id':
                  element.dataset.variantId = productVariant.id;
                  break;

                case 'handle':
                  element.dataset.productHandle = product.handle;

                  checkWishlistStatus(wishlistButton, product, productVariant);

                  break;

                case 'url':
                  element.setAttribute('href', `${product.url}?variant=${productVariant.id}`);
                  break;
              }
            }
          });
        };

        if (variantsCache[productHandle]) {
          loadProduct(variantsCache[productHandle]);
        } else {
          getProduct({ handle: productHandle })
            .then((product) => {
              variantsCache[productHandle] = product;
              loadProduct(product);
            })
            .catch((err) => {
              console.error('Error fetching product images:', err);
            });
        }
      });
    }
  });
};

if (!window.dAgency) window.dAgency = {};
window.dAgency.initProductTile = (context) => {
  const snippetContainer = context.classList.contains('product-tile__container')
    ? context
    : context.closest('.product-tile__container');
  if (!snippetContainer || snippetContainer.classList.contains('product-tile__container--initialized')) return;

  const swiperWrapper = snippetContainer.querySelector('.product-tile__gallery .swiper-wrapper');
  // We always initialize swiper, even for 1 slide, to ensure color swatches work correctly.
  if (swiperWrapper && swiperWrapper.querySelectorAll('.swiper-slide').length > 0) {
    const swiperContainer = swiperWrapper.closest('.product-tile__gallery');
    const pagination = swiperContainer.querySelector('.product-tile__gallery-pagination');
    const options = {
      modules: [Manipulation, EffectFade, Navigation, ...(pagination ? [Pagination] : [])],
      crossFade: true,
      effect: 'fade',
      navigation: {
        prevEl: swiperContainer.querySelector('.product-tile__gallery-arrow--left'),
        nextEl: swiperContainer.querySelector('.product-tile__gallery-arrow--right'),
      },
      ...(pagination
        ? {
            pagination: {
              el: pagination,
            },
          }
        : {}),
    };
    var autoChange = true;
    var changeEnter = false;
    var changeLeave = false;
    const swiper = new Swiper(swiperContainer, options);
    swiperContainer.addEventListener('mouseenter', () => {
      if (window.innerWidth <= parseInt(breakpoints.lg) || !autoChange) return;
      changeEnter = true;
      swiper.slideTo(1);
    });

    swiperContainer.addEventListener('mouseleave', () => {
      if (window.innerWidth <= parseInt(breakpoints.lg) || !autoChange) return;
      changeLeave = true;
      swiper.slideTo(0);
    });

    swiper.on('slideChangeTransitionStart', () => {
      if (!changeEnter && !changeLeave) {
        autoChange = false;
        return;
      }
      if (changeEnter) changeEnter = false;
      if (changeLeave) changeLeave = false;
    });

    manageSwatchColors({ tile: snippetContainer, tileGallery: swiper });
  }

  const quickBuy = snippetContainer.querySelector('.product-tile__quick-buy');
  const form = snippetContainer.querySelector('.product-tile__form');
  const wishlistButton = snippetContainer.querySelector('[data-wishlist-btn]');
  if (quickBuy && form)
    initProductForm({
      form,
      callbacks: {
        afterVariantChange: async ({ variant, product }) => {
          if (wishlistButton) {
            wishlistButton.dataset.wishlistVariantId = variant?.id;
            wishlistButton.dataset.wishlistPrice = formatPrice({
              value: variant?.price ?? 0,
            });
            wishlistButton.dataset.wishlistCompareAtPrice = variant?.compare_at_price
              ? formatPrice({
                  value: variant?.compare_at_price,
                })
              : '';

            const wishlist = await getWishlist();
            const wishlistProduct = wishlist?.listcontents?.find(
              (addedProduct) => addedProduct.epi == variant.id && addedProduct.empi == product.id,
            );
            wishlistButton.dataset.wishlistStatus = wishlistProduct ? 'active' : 'inactive';
          }
        },
        afterSubmit: () => {
          const quickBuyClose = snippetContainer.querySelector('.quick-buy__container.open .modal__close');
          if (quickBuyClose) quickBuyClose.click();
          openMinicart(true);
        },
      },
    });

  snippetContainer.classList.add('product-tile__container--initialized');

  if (wishlistButton) {
    const toggleLoading = throttle(() => {
      wishlistButton.classList.toggle('loading');
    }, 500);
    wishlistButton.addEventListener('click', async (e) => {
      e.preventDefault();
      toggleLoading();
      const variantID = wishlistButton?.dataset?.wishlistVariantId;
      const masterID = wishlistButton?.dataset?.wishlistProductId;
      const productURL = wishlistButton?.dataset?.wishlistProductUrl;
      const price = wishlistButton?.dataset?.wishlistPrice;
      const compareAtPrice = wishlistButton?.dataset?.wishlistCompareAtPrice;

      if (wishlistButton?.dataset?.wishlistStatus === 'active') {
        await deleteFromWishlist({ variantID, masterID, productURL });
        wishlistButton.dataset.wishlistStatus = 'inactive';
      } else {
        await addToWishlist({
          variantID,
          masterID,
          productURL,
          compareAtPrice,
          price,
        });
        wishlistButton.dataset.wishlistStatus = 'active';
      }
      toggleLoading();
    });
  }
};

execOnLoad(window.dAgency.initProductTile, '.product-tile__container');
